import { useEffect } from "react";
import { useTheme } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import Select from "@components/Select/Select";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateVesselStatus } from "@services/vesselsApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LANG_BASE, service, TEST_ID_BASE } from "../VesselStatus";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vesselId: string;
  dropdownCurrentOption?: string;
};

const TEXTAREA_MAX_CHARS = 1000;

const getRestrictionStatusSchema = (t: TFunction) =>
  z.object({
    reason: z
      .string()
      .max(
        TEXTAREA_MAX_CHARS,
        t("form-validation.max-length", {
          label: t(`${LANG_BASE}.statusReason`),
          max: TEXTAREA_MAX_CHARS,
        }),
      )
      .optional(),
    selectedOption: z
      .string()
      .min(1, t("form-validation.required", { label: t(`${LANG_BASE}.vessel-status`) })),
  });

type RestrictionStatusForm = {
  reason?: string;
  selectedOption: string;
};

const RestrictionStatusSideDrawer = ({
  isOpen,
  onClose,
  onSuccess,
  vesselId,
  dropdownCurrentOption,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);

  const restrictionsLabels = t(`vessel-management.restrictionsLabels`, {
    returnObjects: true,
  }) as Record<string, string>;
  const dropdownOptions = Object.entries(restrictionsLabels).map(([value, label]) => ({
    value,
    label,
    testId: `restriction-${label.toLowerCase().replace(/\s+/g, "")}-option`,
  }));

  const form = useForm<RestrictionStatusForm>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(getRestrictionStatusSchema(t)),
    shouldUnregister: false,
    defaultValues: {
      reason: "",
      selectedOption: dropdownCurrentOption ?? "",
    },
  });

  const { mutate: changeStatus, isPending } = useMutation({
    mutationFn: (payload: Parameters<typeof updateVesselStatus>[0]) =>
      runTransaction(
        {
          module: "vessel_registration",
          screen: "RestrictionStatus",
          action: "update_vessel_status",
          entityType: "vessel",
        },
        (transactionId) =>
          updateVesselStatus({ ...payload, ...(transactionId ? { transactionId } : {}) }),
      ),
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
  });

  const reason = form.watch("reason") ?? "";
  const selectedOption = form.watch("selectedOption");

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onValid = ({ reason, selectedOption }: RestrictionStatusForm) => {
    if (!vesselId) return;

    changeStatus({
      service,
      id: vesselId,
      vesselStatusPayload: {
        vesselLogTypeCode: selectedOption,
        statusChangeReason: reason?.trim() || "",
      },
    });
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  useEffect(() => {
    if (!isOpen) return;
    form.reset();
  }, [isOpen, dropdownCurrentOption, form]);

  const inputSlotProps = {
    root: {
      className: `rounded-lg text-sm px-4 py-2 border`,
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: {
      maxLength: TEXTAREA_MAX_CHARS,
    },
  };

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": {
      padding: 0,
    },
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={onSubmit}
      isSubmitting={isPending}
      keepMounted={false}
      title={t(`${LANG_BASE}.statusTitle`)}
      submitLabel={t("common.ok")}
      description={t(`${LANG_BASE}.statusDescription`)}
    >
      <div className="flex flex-col gap-4 w-[400px] p-6">
        <div className="col-span-full mt-6">
          <CustomFormLabel data-testid="statusLabelId">
            {t(`${LANG_BASE}.vessel-status`)}
          </CustomFormLabel>
          <Select
            options={dropdownOptions || []}
            value={selectedOption}
            onChange={(val) => {
              form.setValue("selectedOption", val, {
                shouldTouch: true,
                shouldValidate: true,
              });
            }}
            testId={`${TEST_ID_BASE}-status-select`}
            placeholder={t(`${LANG_BASE}.vessel-status`)}
            style={{ width: "100%", marginTop: "4px" }}
          />
          {form.formState.errors.selectedOption?.message && (
            <p style={styles.errorText} className="text-sm mt-1">
              {form.formState.errors.selectedOption.message}
            </p>
          )}
        </div>
        <div className="col-span-full mb-0 flex flex-col gap-2">
          <CustomFormLabel data-testid="labelId">{t(`${LANG_BASE}.statusReason`)}</CustomFormLabel>
          <Input
            placeholder={t(`${LANG_BASE}.statusReason`)}
            multiline
            minRows={4}
            fullWidth
            variant="outlined"
            value={reason}
            onChange={(e) => {
              form.setValue("reason", e.target.value, {
                shouldTouch: true,
                shouldValidate: true,
              });
            }}
            slotProps={inputSlotProps}
            sx={sxProps}
            testId={`${TEST_ID_BASE}-reason-input`}
          />
          {form.formState.errors.reason?.message && (
            <p style={styles.errorText} className="text-sm mt-1">
              {form.formState.errors.reason.message}
            </p>
          )}
          <FormHelperText sx={charCounterSx}>
            {reason.length}/{TEXTAREA_MAX_CHARS}
          </FormHelperText>
        </div>
      </div>
    </SideDrawer>
  );
};

export default RestrictionStatusSideDrawer;
